import { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button, TextField, CircularProgress, Alert, Tabs, Tab, Box,
    InputAdornment, IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LocalHostConnection, Machine } from '../../../ts/Connections';
import { useTranslation } from 'react-i18next';

interface LicenseLoginModalProps {
    open: boolean;
    machine: Machine | null;
    onClose: () => void;
    onSuccess: (machine: Machine) => void;
}

type LoginTab = 'credentials' | 'apiKey';

export default function LicenseLoginModal({ open, machine, onClose, onSuccess }: LicenseLoginModalProps) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<LoginTab>('credentials');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setApiKey('');
        setError(null);
        setShowPassword(false);
        setShowApiKey(false);
    };

    const handleClose = () => {
        if (submitting) return;
        resetForm();
        onClose();
    };

    const handleTabChange = (_: React.SyntheticEvent, value: LoginTab) => {
        if (submitting) return;
        setError(null);
        setTab(value);
    };

    const submit = async (request: () => Promise<any>) => {
        if (!machine || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const response: any = await request();
            // The backend now returns the appropriate error code (e.g. LIC_LOGIN_FAILURE)
            // when credentials are invalid, so a code of "0" means the license was checked out.
            const setLoginInfo = response?.['Responses']?.['SetLoginInfo'];
            const licensed = setLoginInfo?.['code'] === '0';
            if (licensed) {
                resetForm();
                onSuccess(machine);
            } else {
                // Surface the machine's own description so the user understands why it failed.
                setError(setLoginInfo?.['description'] || t('matchmaking.licenseModal.error'));
            }
        } catch {
            setError(t('matchmaking.licenseModal.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const credentialsReady = email.trim() !== '' && password.trim() !== '';
    const apiKeyReady = apiKey.trim() !== '';

    const handleCredentialsSubmit = () => {
        if (!credentialsReady || !machine) return;
        const connection = machine.connection as LocalHostConnection;
        submit(() => connection.setLoginInfoWithCredentials(email.trim(), password.trim()));
    };

    const handleApiKeySubmit = () => {
        if (!apiKeyReady || !machine) return;
        const connection = machine.connection as LocalHostConnection;
        submit(() => connection.setLoginInfoWithKey(apiKey.trim()));
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>{t('matchmaking.licenseModal.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t('matchmaking.licenseModal.message')}
                </DialogContentText>
                <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 2 }}>
                    <Tab value="credentials" label={t('matchmaking.licenseModal.credentialsTab')} disabled={submitting} />
                    <Tab value="apiKey" label={t('matchmaking.licenseModal.apiKeyTab')} disabled={submitting} />
                </Tabs>

                {tab === 'credentials' && (
                    <Box>
                        <TextField
                            autoFocus
                            fullWidth
                            label={t('matchmaking.licenseModal.emailLabel')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={submitting}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            label={t('matchmaking.licenseModal.passwordLabel')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCredentialsSubmit(); }}
                            disabled={submitting}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={t('matchmaking.licenseModal.togglePasswordVisibility') ?? ''}
                                            aria-pressed={showPassword}
                                            onClick={() => setShowPassword((show) => !show)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                )}

                {tab === 'apiKey' && (
                    <Box>
                        <TextField
                            autoFocus
                            fullWidth
                            type={showApiKey ? 'text' : 'password'}
                            autoComplete="off"
                            label={t('matchmaking.licenseModal.apiKeyLabel')}
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApiKeySubmit(); }}
                            disabled={submitting}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={t('matchmaking.licenseModal.toggleApiKeyVisibility') ?? ''}
                                            aria-pressed={showApiKey}
                                            onClick={() => setShowApiKey((show) => !show)}
                                            edge="end"
                                        >
                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting} color="inherit">
                    {t('alertContext.cancel')}
                </Button>
                {tab === 'credentials' ? (
                    <Button
                        onClick={handleCredentialsSubmit}
                        disabled={submitting || !credentialsReady}
                        variant="contained"
                    >
                        {submitting ? <CircularProgress size={20} /> : t('matchmaking.licenseModal.submit')}
                    </Button>
                ) : (
                    <Button
                        onClick={handleApiKeySubmit}
                        disabled={submitting || !apiKeyReady}
                        variant="contained"
                    >
                        {submitting ? <CircularProgress size={20} /> : t('matchmaking.licenseModal.submit')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
